import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  controlerTexteCgu,
  creerBrouillonCgu,
  enregistrerBrouillonCgu,
  etatVersion,
  LIBELLE_ETAT_CGU,
  libelleAcceptations,
  listerDecomptesCgu,
  listerVersionsCgu,
  obstaclePublication,
  publierCgu,
  supprimerBrouillonCgu,
  type ChampsCgu,
  type DecompteCgu,
  type VersionCgu,
} from '../../api/cgu'
import { messageErreur } from '../../onboarding/erreurs'
import { dateCourte } from '../Format'
import { Compteur } from '../Compteur'
import { Erreur, Info, useAction } from '../candidatures/Briques'
import { MarkdownRendu } from '../candidatures/MarkdownRendu'

const TEXTE_DEPART = "# Conditions générales d'utilisation\n\n## Article 1\n\nRédigez ici le texte.\n"

const BADGE = { brouillon: 'badge-yellow', en_vigueur: 'badge-green', archivee: 'badge-gray' } as const

/**
 * CGU clients — RÉSERVÉ AUX ADMINS (la base aussi : policies et fonctions).
 *
 * Les versions vivent EN INTERNE : les clients voient le texte en vigueur, jamais un numéro de version ni un
 * historique. Ici : éditer le brouillon, le publier (l'ancienne version reste lisible, en lecture seule),
 * suivre combien de clients ont accepté chaque version. Une version publiée est IMMUABLE.
 */
export function EcranCguClients() {
  const [versions, setVersions] = useState<VersionCgu[]>([])
  const [decomptes, setDecomptes] = useState<Record<string, DecompteCgu>>({})
  const [choisiId, setChoisiId] = useState<string | null>(null)
  const [erreurLecture, setErreurLecture] = useState<string | null>(null)
  const [chargee, setChargee] = useState(false)

  const charger = useCallback(async (selection?: string) => {
    /* Deux lectures INDÉPENDANTES : les décomptes qui échouent ne cachent pas les textes. */
    const [v, d] = await Promise.allSettled([listerVersionsCgu(), listerDecomptesCgu()])
    if (v.status === 'fulfilled') {
      setVersions(v.value)
      setChoisiId((courant) =>
        selection ??
        (courant && v.value.some((x) => x.id === courant) ? courant : null) ??
        v.value.find((x) => x.publieeLe === null)?.id ??
        v.value[0]?.id ??
        null,
      )
    }
    if (d.status === 'fulfilled') setDecomptes(Object.fromEntries(d.value.map((x) => [x.versionId, x])))
    setErreurLecture(
      v.status === 'rejected' ? messageErreur(v.reason) : d.status === 'rejected' ? `Décomptes indisponibles : ${messageErreur(d.reason)}` : null,
    )
    setChargee(true)
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const enVigueur = versions.find((v) => v.enVigueur) ?? null
  const brouillon = versions.find((v) => v.publieeLe === null) ?? null
  const choisi = versions.find((v) => v.id === choisiId) ?? null
  const decompteVigueur = enVigueur ? decomptes[enVigueur.id] : undefined
  const nbClients = Object.values(decomptes)[0]?.nbClients ?? 0

  return (
    <>
      <div className="stats">
        <div className="stat" title="Le texte que les clients voient et doivent avoir accepté pour commander">
          <div className="label">En vigueur</div>
          <div className="val" style={{ fontSize: 16, color: enVigueur ? 'var(--green)' : 'var(--n400)' }}>
            {enVigueur ? enVigueur.version : 'Aucune'}
          </div>
        </div>
        <div className="stat" title="Clients ayant accepté la version en vigueur">
          <div className="label">Acceptations (version en vigueur)</div>
          <div className="val"><Compteur valeur={String(decompteVigueur?.nbAcceptations ?? 0)} /></div>
        </div>
        <div className="stat">
          <div className="label">Clients enregistrés</div>
          <div className="val"><Compteur valeur={String(nbClients)} /></div>
        </div>
        <div className="stat">
          <div className="label">Brouillon</div>
          <div className="val" style={{ fontSize: 16, color: brouillon ? 'var(--warning)' : 'var(--n400)' }}>
            {brouillon ? brouillon.version : 'Aucun'}
          </div>
        </div>
      </div>

      {chargee && !enVigueur && (
        <div className="dos-alerte" style={{ marginTop: 0, marginBottom: 12 }}>
          <b>Aucune version n'est en vigueur.</b> Tant que le juriste n'a pas relu et publié un texte, les clients ne
          voient aucune case à cocher à l'inscription et personne n'est bloqué pour commander.
        </div>
      )}
      {enVigueur && (
        <div className="dos-note" style={{ marginBottom: 12 }}>
          À la publication d'une nouvelle version, chaque client voit « Nos conditions ont évolué » à sa prochaine
          connexion et ne peut plus commander tant qu'il n'a pas accepté. Les clients ne voient ni numéro de version ni
          historique.
        </div>
      )}
      <Erreur texte={erreurLecture} />

      <div className="card">
        <div className="card-header">
          CGU clients
          <div className="spacer" style={{ flex: 1 }} />
          <NouveauBrouillon
            versions={versions}
            enVigueur={enVigueur}
            desactive={brouillon !== null}
            surCree={(v) => void charger(v.id)}
          />
        </div>
        <div className="card-body" style={{ padding: 16 }}>
          <div className="mod-grille">
            <div className="mod-liste">
              {versions.map((v) => {
                const etat = etatVersion(v)
                const d = decomptes[v.id]
                return (
                  <button key={v.id} className={`mod-item${v.id === choisiId ? ' on' : ''}`} onClick={() => setChoisiId(v.id)}>
                    <b>{v.version}</b>
                    <span className="sous">
                      <span className={`badge ${BADGE[etat]}`}>{LIBELLE_ETAT_CGU[etat]}</span>
                      {etat === 'brouillon' && v.provisoire && <span className="badge badge-red">Provisoire</span>}
                    </span>
                    <span className="dos-note" style={{ display: 'block', marginTop: 4 }}>
                      {v.publieeLe ? `Publiée ${dateCourte(v.publieeLe)} · ` : ''}
                      {etat === 'brouillon' ? 'Pas encore soumise aux clients' : libelleAcceptations(d?.nbAcceptations ?? 0, d?.nbClients ?? nbClients)}
                    </span>
                  </button>
                )
              })}
              {chargee && versions.length === 0 && <p className="dos-note">Aucune version.</p>}
            </div>
            <div>
              {choisi ? (
                <EditeurCgu
                  key={choisi.id}
                  version={choisi}
                  enVigueur={enVigueur}
                  decompte={decomptes[choisi.id]}
                  nbClients={nbClients}
                  surChange={(suivant) => void charger(suivant)}
                />
              ) : (
                <p className="dos-note">Choisissez une version à gauche{brouillon ? '' : ', ou créez un brouillon'}.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function NouveauBrouillon({
  versions,
  enVigueur,
  desactive,
  surCree,
}: {
  versions: VersionCgu[]
  enVigueur: VersionCgu | null
  desactive: boolean
  surCree: (v: VersionCgu) => void
}) {
  const { occupe, erreur, lancer } = useAction()
  return (
    <>
      <button
        className="btn btn-outline btn-sm"
        disabled={occupe || desactive}
        title={
          desactive
            ? 'Un brouillon est déjà en cours : modifiez-le ou publiez-le.'
            : 'Nouveau brouillon, copié du texte en vigueur pour repartir de lui'
        }
        onClick={() =>
          void lancer(async () => {
            const champs: ChampsCgu = { corps: enVigueur?.corps ?? TEXTE_DEPART, provisoire: enVigueur === null }
            surCree(await creerBrouillonCgu(versions.map((v) => v.version), champs))
          })
        }
      >
        + Nouveau brouillon
      </button>
      {erreur && <span className="dos-note" style={{ color: 'var(--rouge)' }}>{erreur}</span>}
    </>
  )
}

function EditeurCgu({
  version,
  enVigueur,
  decompte,
  nbClients,
  surChange,
}: {
  version: VersionCgu
  enVigueur: VersionCgu | null
  decompte: DecompteCgu | undefined
  nbClients: number
  surChange: (suivantId?: string) => void
}) {
  const publiee = version.publieeLe !== null
  const etat = etatVersion(version)
  const [corps, setCorps] = useState(version.corps)
  const [provisoire, setProvisoire] = useState(version.provisoire)
  const [apercu, setApercu] = useState(publiee)
  const { occupe, erreur, info, lancer, signaler } = useAction()
  const controle = useMemo(() => controlerTexteCgu(corps), [corps])
  const champs = (): ChampsCgu => ({ corps, provisoire })

  function enregistrer() {
    void lancer(async () => {
      await enregistrerBrouillonCgu(version.id, champs())
      surChange(version.id)
      return 'Brouillon enregistré.'
    })
  }

  function publier() {
    const obstacle = obstaclePublication(version, corps, provisoire, enVigueur)
    if (obstacle) return signaler(obstacle)
    const avertissement = controle.crochets.length
      ? `Il reste ${controle.crochets.length} passage(s) entre crochets à compléter :\n  ${controle.crochets.slice(0, 8).join('\n  ')}${controle.crochets.length > 8 ? '\n  …' : ''}\n\n`
      : ''
    if (
      !window.confirm(
        `${avertissement}Publier ${version.version} ?\n\nElle devient le texte en vigueur : les clients devront l'accepter (à l'inscription, puis à leur prochaine connexion avant de commander). Elle ne pourra plus être modifiée.`,
      )
    ) {
      return
    }
    void lancer(async () => {
      await publierCgu(version.id, champs())
      surChange(version.id)
      return 'Version publiée : elle est désormais en vigueur.'
    })
  }

  function supprimer() {
    if (!window.confirm(`Supprimer le brouillon ${version.version} ?`)) return
    void lancer(async () => {
      await supprimerBrouillonCgu(version.id)
      surChange()
    })
  }

  return (
    <>
      <div className="sous-liens" style={{ marginBottom: 8 }}>
        <b>{version.version}</b>
        <span className={`badge ${BADGE[etat]}`}>{LIBELLE_ETAT_CGU[etat]}</span>
        {version.publieeLe && <span className="dos-note">publiée le {dateCourte(version.publieeLe)}</span>}
      </div>
      {publiee ? (
        <>
          <p className="dos-note" style={{ marginBottom: 8 }}>
            Version publiée : lecture seule. {libelleAcceptations(decompte?.nbAcceptations ?? 0, decompte?.nbClients ?? nbClients)}
            {decompte?.derniereAcceptation ? ` — dernière acceptation le ${dateCourte(decompte.derniereAcceptation)}` : ''}.
            {etat === 'archivee' ? " Elle n'est plus en vigueur : une version plus récente l'a remplacée." : ''}
          </p>
          <div className="dos-bloc" style={{ marginBottom: 0 }}>
            <MarkdownRendu source={version.corps} />
          </div>
        </>
      ) : (
        <>
          <label className="sous-liens" style={{ margin: '4px 0' }}>
            <input type="checkbox" checked={provisoire} onChange={(e) => setProvisoire(e.target.checked)} />
            Texte provisoire (empêche la publication) — à décocher une fois relu par le juriste
          </label>
          <div className="sous-liens" style={{ margin: '10px 0 4px' }}>
            <button className={`chip${!apercu ? ' active' : ''}`} onClick={() => setApercu(false)}>Édition</button>
            <button className={`chip${apercu ? ' active' : ''}`} onClick={() => setApercu(true)}>Aperçu</button>
          </div>
          {apercu ? (
            <div className="dos-bloc" style={{ marginBottom: 0 }}>
              <MarkdownRendu source={corps} />
            </div>
          ) : (
            <textarea
              rows={20}
              value={corps}
              onChange={(e) => setCorps(e.target.value)}
              aria-label="Texte des conditions générales (markdown)"
              style={{ width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 }}
            />
          )}
          {controle.crochets.length > 0 && (
            <div className="mod-avert info">
              <b>{controle.crochets.length} passage(s) entre crochets à compléter avant publication :</b>{' '}
              {controle.crochets.slice(0, 6).map((c, i) => <code key={i}>{c} </code>)}
              {controle.crochets.length > 6 && '…'}
            </div>
          )}
          <div className="dos-champs">
            <button className="btn btn-outline btn-sm" disabled={occupe} onClick={enregistrer}>Enregistrer le brouillon</button>
            <button className="btn btn-accent btn-sm" disabled={occupe} onClick={publier}>Publier</button>
            <button className="btn btn-outline btn-sm" disabled={occupe} onClick={supprimer}>Supprimer le brouillon</button>
          </div>
        </>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </>
  )
}
